import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDateTime } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import type { SystemHealthSnapshot } from '../types';
import SectionCard from './SectionCard';
import { formatDuration } from '../utils';
import {
  zoneLinkClass,
  zoneStatTileClass,
  zoneStatTileLabelClass,
  zoneTableRowClass,
  type OpsIntensity,
} from '../theme';

interface SyncStatsPanelProps {
  syncStats: SystemHealthSnapshot['syncStats'];
  embedded?: boolean;
}

const StatTile: React.FC<{
  label: string;
  hint?: string;
  value: string | number;
  tone?: 'good' | 'warn' | 'bad' | 'neutral';
}> = ({ label, hint, value, tone = 'neutral' }) => {
  const { colors } = useTheme();
  const valueClass =
    tone === 'good'
      ? 'text-emerald-700 dark:text-emerald-300'
      : tone === 'warn'
        ? 'text-amber-700 dark:text-amber-300'
        : tone === 'bad'
          ? 'text-rose-700 dark:text-rose-300'
          : colors.text;

  return (
    <div className={zoneStatTileClass('mail')} title={hint}>
      <p className={zoneStatTileLabelClass('mail')}>{label}</p>
      <p className={cn('text-xl font-bold tabular-nums mt-0.5', valueClass)}>{value}</p>
      {hint && <p className={cn('text-[10px] mt-1 leading-snug', colors.textMuted)}>{hint}</p>}
    </div>
  );
};

function formatCount(n: number): string {
  return n.toLocaleString();
}

export const SyncStatsPanel: React.FC<SyncStatsPanelProps> = ({ syncStats, embedded = false }) => {
  const { colors } = useTheme();
  const { last24h, byAccount = [], syncEnabledAccounts = 0, expectedRunsPerDay = 0 } = syncStats;

  const contextLine =
    syncEnabledAccounts === 0
      ? 'No mailboxes with sync enabled'
      : `${syncEnabledAccounts} mailbox${syncEnabledAccounts === 1 ? '' : 'es'} · ~${formatCount(expectedRunsPerDay)} scheduled runs/day · rolling 24h window`;

  return (
    <>
      <p className={cn('text-xs mb-3 font-medium text-sky-800/70 dark:text-sky-300/80')}>{contextLine}</p>

      <p className={cn('text-[10px] uppercase tracking-wider font-semibold mb-2 text-sky-700/80 dark:text-sky-300/70')}>
        Mail synced (successful runs)
      </p>
      <div className={cn('grid gap-3 mb-4', embedded ? 'grid-cols-3' : 'grid-cols-3 sm:grid-cols-3')}>
        <StatTile
          label="Messages saved"
          value={formatCount(last24h.emailsSaved ?? 0)}
          hint="New/updated messages written"
          tone="good"
        />
        <StatTile
          label="Messages scanned"
          value={formatCount(last24h.emailsFound ?? 0)}
          hint="Seen on the server during sync"
        />
        <StatTile
          label="Save failures"
          value={formatCount(last24h.emailsFailed ?? 0)}
          tone={(last24h.emailsFailed ?? 0) > 0 ? 'bad' : 'neutral'}
        />
      </div>

      <p className={cn('text-[10px] uppercase tracking-wider font-semibold mb-2 text-sky-700/80 dark:text-sky-300/70')}>
        Sync runs (log entries)
      </p>
      <div className={cn('grid gap-3', embedded ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4')}>
        <StatTile
          label="Runs OK"
          value={last24h.completed}
          hint={`~${formatCount(expectedRunsPerDay)}/day expected`}
          tone="good"
        />
        <StatTile label="Run errors" value={last24h.error} tone={last24h.error > 0 ? 'bad' : 'neutral'} />
        <StatTile
          label="Open runs"
          value={last24h.started}
          hint="High count may mean a stuck sync"
          tone={last24h.started > 2 ? 'warn' : 'neutral'}
        />
        <StatTile label="Warnings" value={last24h.warning} tone={last24h.warning > 0 ? 'warn' : 'neutral'} />
      </div>

      {byAccount.length > 0 && (
        <div className={cn('mt-4 rounded-lg border overflow-hidden border-sky-200/60 dark:border-sky-800/40')}>
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold bg-sky-100/80 dark:bg-sky-900/35 text-sky-900/70 dark:text-sky-200/80">
            Per mailbox (runs OK in 24h)
          </div>
          <ul className="divide-y divide-sky-100 dark:divide-sky-900/50">
            {byAccount.map((row, i) => (
              <li
                key={row.accountId}
                className={cn(
                  'px-3 py-2 text-sm flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5',
                  colors.text,
                  zoneTableRowClass('mail', { zebra: true }),
                  i % 2 === 1 && 'bg-sky-50/40 dark:bg-sky-950/15',
                )}
              >
                <span className="font-medium truncate min-w-0">{row.emailAddress}</span>
                <span className={cn('tabular-nums text-xs shrink-0', colors.textMuted)}>
                  every {row.syncIntervalMinutes} min ·{' '}
                  <span className={cn('font-semibold', colors.text)}>{row.completedRuns}</span>
                  /~{row.expectedRunsPerDay} runs
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div
        className={cn(
          'mt-4 grid gap-3 text-sm',
          embedded ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2',
          colors.textMuted,
        )}
      >
        <div className={colors.text}>
          <span className="font-medium">Last successful sync</span>
          <span className={cn('ml-1.5', colors.textMuted)}>·</span>
          <span className="ml-1.5 tabular-nums">
            {syncStats.lastSuccessfulSyncAt ? formatDateTime(syncStats.lastSuccessfulSyncAt) : 'Never'}
          </span>
        </div>
        <div>
          <span className="font-medium">Avg run duration (24h)</span>
          <p className={cn('mt-0.5', colors.text)}>
            {syncStats.avgCompletedDurationMs != null
              ? formatDuration(Math.round(syncStats.avgCompletedDurationMs / 1000))
              : '—'}
          </p>
        </div>
      </div>
    </>
  );
};

interface SyncStatsCardProps {
  syncStats: SystemHealthSnapshot['syncStats'];
  intensity?: OpsIntensity;
}

const SyncStatsCard: React.FC<SyncStatsCardProps> = ({ syncStats, intensity = 'admin' }) => {
  return (
    <SectionCard
      zone="mail"
      intensity={intensity}
      title="Sync statistics (24h)"
      subtitle="Rolling window — mail activity and scheduled sync runs"
      action={
        <Link to="/admin/email-logs" className={zoneLinkClass('mail')}>
          Full logs →
        </Link>
      }
    >
      <SyncStatsPanel syncStats={syncStats} />
    </SectionCard>
  );
};

export default SyncStatsCard;
